import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    project_description: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        project_description: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '(240) 370-9029',
      href: 'tel:2403709029',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'info@luxrenovations.com',
      href: 'mailto:info@luxrenovations.com',
    },
    {
      icon: MapPin,
      label: 'Service Area',
      value: 'Gaithersburg, MD & Surrounding Areas',
      href: null,
    },
    {
      icon: Clock,
      label: 'Hours',
      value: 'Mon - Sat: 8AM - 6PM',
      href: null,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="section-padding bg-[#0A0A0A]"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#D4AF37] text-sm font-medium tracking-[0.2em] uppercase mb-4">
              Get In Touch
            </p>
            <h2
              data-testid="contact-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-6 leading-tight"
            >
              Schedule Your Free
              <br />
              Consultation
            </h2>
            <div className="w-16 h-0.5 bg-[#D4AF37] mb-8" />
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10">
              Ready to transform your home? Contact us today for a free
              consultation. We'll discuss your vision, assess your space, and
              provide a detailed estimate—all with no obligation.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm mb-1">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-white hover:text-[#D4AF37] transition-colors font-medium"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white font-medium">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Service Areas */}
            <div className="mt-10 pt-10 border-t border-white/10">
              <p className="text-white/50 text-sm mb-4">Serving:</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Gaithersburg • Rockville • Bethesda • Potomac • Silver Spring •
                Germantown • Clarksburg • Damascus
              </p>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-white p-8 md:p-10">
              <h3 className="font-serif text-2xl text-[#0A0A0A] font-bold mb-6">
                Request a Free Quote
              </h3>

              {submitStatus === 'success' && (
                <div
                  data-testid="form-success-message"
                  className="mb-6 p-4 bg-green-50 border border-green-200 flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800 font-medium">
                      Thank you for your inquiry!
                    </p>
                    <p className="text-green-600 text-sm mt-1">
                      We'll contact you within 24 hours to schedule your free
                      consultation.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div
                  data-testid="form-error-message"
                  className="mb-6 p-4 bg-red-50 border border-red-200 flex items-start gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium">
                      Something went wrong
                    </p>
                    <p className="text-red-600 text-sm mt-1">
                      Please try again or call us directly at (240) 370-9029.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-[#1A1A1A] font-medium mb-2 block"
                    >
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      data-testid="contact-name-input"
                      placeholder="John Smith"
                      className="rounded-none border-[#E4E4E7] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                  </div>

                  {/* Phone & Email */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label
                        htmlFor="phone"
                        className="text-[#1A1A1A] font-medium mb-2 block"
                      >
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        data-testid="contact-phone-input"
                        placeholder="(240) 555-0123"
                        className="rounded-none border-[#E4E4E7] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="email"
                        className="text-[#1A1A1A] font-medium mb-2 block"
                      >
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        data-testid="contact-email-input"
                        placeholder="john@example.com"
                        className="rounded-none border-[#E4E4E7] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                    </div>
                  </div>

                  {/* Project Description */}
                  <div>
                    <Label
                      htmlFor="project_description"
                      className="text-[#1A1A1A] font-medium mb-2 block"
                    >
                      Tell Us About Your Project *
                    </Label>
                    <Textarea
                      id="project_description"
                      name="project_description"
                      required
                      value={formData.project_description}
                      onChange={handleInputChange}
                      data-testid="contact-project-input"
                      placeholder="Describe your renovation project, including the type of work you need and any specific requirements..."
                      rows={5}
                      className="rounded-none border-[#E4E4E7] focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C9A227] rounded-none py-6 text-base font-medium flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Get a Free Consultation
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-[#71717A] text-xs text-center">
                    By submitting this form, you agree to be contacted regarding
                    your project. We respect your privacy.
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
